import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Avatar,
  Card,
  CardContent,
  CardActions,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  GridView as GridViewIcon,
  TableChart as TableChartIcon,
  Clear as ClearIcon,
  Storefront as StorefrontIcon,
  Smartphone as SmartphoneIcon,
  LocalPhone as LocalPhoneIcon,
  PinDrop as PinDropIcon,
} from '@mui/icons-material';

import PickUpPointsDetailsModal from '../../AddOrUpdate/components/PickUpPointsDetailsModal';

const PickUpPointsDetailsView = ({ reactTableData, isNoRecordFound, onRefresh }) => {
  const rows = useMemo(() => {
    return reactTableData && reactTableData.tableRowsData ? reactTableData.tableRowsData : [];
  }, [reactTableData]);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal State
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);

  const handleAdd = () => {
    setSelectedPoint(null);
    setModalTitle('Add - Pick Up Point Details');
    setViewDetailPopup(true);
  };

  const handleEdit = point => {
    setSelectedPoint(point);
    setModalTitle('Edit - Pick Up Point Details');
    setViewDetailPopup(true);
  };

  // Filter rows based on search term across all main attributes
  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(item => {
      const name = item?.collectionPointName ? item.collectionPointName.toLowerCase() : '';
      const code = item?.collectionPointCode ? item.collectionPointCode.toLowerCase() : '';
      const contactName = item?.collectionPointContactName ? item.collectionPointContactName.toLowerCase() : '';
      const mobile = item?.collectionPointContactMobileNo ? String(item.collectionPointContactMobileNo).toLowerCase() : '';
      const phone = item?.collectionPointPhoneNo ? String(item.collectionPointPhoneNo).toLowerCase() : '';
      const address = item?.collectionPointAddress ? item.collectionPointAddress.toLowerCase() : '';
      const grl = item?.collectionPointGrl ? String(item.collectionPointGrl).toLowerCase() : '';

      return (
        name.includes(term) ||
        code.includes(term) ||
        contactName.includes(term) ||
        mobile.includes(term) ||
        phone.includes(term) ||
        address.includes(term) ||
        grl.includes(term)
      );
    });
  }, [rows, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Page Header & Controls Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          {/* Title & Count */}
          <Grid item xs={12} md={4} display="flex" alignItems="center" gap={1.5}>
            <Avatar
              sx={{
                bgcolor: '#32CA73',
                width: 48,
                height: 48,
                boxShadow: '0 4px 12px rgba(50, 202, 115, 0.35)',
              }}
            >
              <StorefrontIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b', fontSize: '1.25rem', lineHeight: 1.2 }}>
                Pick Up Point Details
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                Manage merchant collection centers, warehouse hubs, and contact details
              </Typography>
            </Box>
            <Chip
              label={`${filteredRows.length} Total`}
              size="small"
              sx={{
                bgcolor: '#dcfce7',
                color: '#15803d',
                fontWeight: 700,
                fontSize: '0.75rem',
                ml: 1,
              }}
            />
          </Grid>

          {/* Search, View Toggle, Add Action */}
          <Grid item xs={12} md={8} display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1.5} flexWrap="wrap">
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search Location, Code, Mobile..."
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  fontSize: '0.875rem',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Table / Grid Toggle Buttons */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) setViewMode(newMode);
              }}
              size="small"
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                border: '1px solid #e2e8f0',
                p: '2px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: '#64748b',
                  '&.Mui-selected': {
                    bgcolor: '#32CA73',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#28b063',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <TableChartIcon sx={{ fontSize: 18, mr: 0.5 }} /> Table View
              </ToggleButton>
              <ToggleButton value="grid">
                <GridViewIcon sx={{ fontSize: 18, mr: 0.5 }} /> Grid View
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Add Pick Up Point Button */}
            <Button
              variant="contained"
              onClick={handleAdd}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2.5,
                px: 2.5,
                py: 0.85,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                background: 'linear-gradient(135deg, #32CA73 0%, #21b368 100%)',
                boxShadow: '0 4px 12px rgba(50, 202, 115, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #28b063 0%, #1b9b59 100%)',
                  boxShadow: '0 6px 16px rgba(50, 202, 115, 0.45)',
                },
              }}
            >
              Add Pick Up Point
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content: Table View vs Grid View */}
      {isNoRecordFound || filteredRows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#fff',
          }}
        >
          <StorefrontIcon sx={{ fontSize: 52, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="textSecondary" fontWeight={600}>
            No Pick Up Point Records Found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            {searchTerm
              ? 'No matching locations found for your search query. Try clearing the filter.'
              : 'Click "Add Pick Up Point" to register your first pick up location.'}
          </Typography>
        </Paper>
      ) : viewMode === 'table' ? (
        /* TABLE VIEW */
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 850 }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5, width: 60 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Location Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Location Code</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Contact Person</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Mobile No.</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Telephone</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>GRL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, index) => {
                  const serialNo = page * rowsPerPage + index + 1;
                  return (
                    <TableRow
                      key={row.pickUpPointPointId || row.collectionPointCode || index}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>{serialNo}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: '#ecfdf5',
                              color: '#059669',
                              fontSize: '0.875rem',
                              fontWeight: 700,
                              boxShadow: '0 2px 6px rgba(5, 150, 105, 0.15)',
                            }}
                          >
                            {row.collectionPointName ? row.collectionPointName.charAt(0).toUpperCase() : 'P'}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {row.collectionPointName || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.collectionPointCode || 'N/A'}
                          size="small"
                          sx={{
                            bgcolor: '#f1f5f9',
                            color: '#334155',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: 1.5,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} color="#475569">
                          <PersonIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                          <Typography variant="body2">{row.collectionPointContactName || '-'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} color="#475569">
                          <SmartphoneIcon sx={{ fontSize: 16, color: '#0284c7' }} />
                          <Typography variant="body2" fontWeight={500}>
                            {row.collectionPointContactMobileNo || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} color="#64748b">
                          <LocalPhoneIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                          <Typography variant="body2">{row.collectionPointPhoneNo || '-'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Tooltip title={row.collectionPointAddress || ''} placement="top">
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationOnIcon sx={{ fontSize: 16, color: '#ef4444', flexShrink: 0 }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#475569',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.collectionPointAddress || '-'}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {row.collectionPointGrl ? (
                          <Chip
                            label={row.collectionPointGrl}
                            size="small"
                            sx={{
                              bgcolor: '#fef3c7',
                              color: '#b45309',
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              borderRadius: 1.5,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Pick Up Point Details">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                            sx={{
                              color: '#0284c7',
                              bgcolor: '#f0f9ff',
                              '&:hover': { bgcolor: '#e0f2fe' },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid #e2e8f0' }}
          />
        </Paper>
      ) : (
        /* GRID VIEW (CARD VIEW) */
        <Box>
          <Grid container spacing={2.5}>
            {paginatedRows.map((row, index) => {
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={row.pickUpPointPointId || row.collectionPointCode || index}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.25s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      bgcolor: '#fff',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                        borderColor: '#cbd5e1',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5, pb: 1.5 }}>
                      {/* Card Header */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                        <Box display="flex" alignItems="center" gap={1.25}>
                          <Avatar
                            sx={{
                              bgcolor: '#ecfdf5',
                              color: '#059669',
                              width: 40,
                              height: 40,
                              fontWeight: 700,
                              fontSize: '1rem',
                            }}
                          >
                            {row.collectionPointName ? row.collectionPointName.charAt(0).toUpperCase() : 'P'}
                          </Avatar>
                          <Box sx={{ maxWidth: 140 }}>
                            <Tooltip title={row.collectionPointName || ''} placement="top">
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                color="#1e293b"
                                noWrap
                              >
                                {row.collectionPointName || '-'}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="#64748b" display="block">
                              Pick Up Center
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={row.collectionPointCode || 'N/A'}
                          size="small"
                          sx={{
                            bgcolor: '#f1f5f9',
                            color: '#334155',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            borderRadius: 1.5,
                          }}
                        />
                      </Box>

                      <Divider sx={{ my: 1.5, borderColor: '#f1f5f9' }} />

                      {/* Details List */}
                      <Box display="flex" flexDirection="column" gap={1}>
                        {/* Contact Person */}
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon sx={{ fontSize: 17, color: '#94a3b8' }} />
                          <Typography variant="body2" color="#334155">
                            {row.collectionPointContactName || 'No Contact Specified'}
                          </Typography>
                        </Box>

                        {/* Mobile Number */}
                        <Box display="flex" alignItems="center" gap={1}>
                          <SmartphoneIcon sx={{ fontSize: 17, color: '#0284c7' }} />
                          <Typography variant="body2" fontWeight={600} color="#0f172a">
                            {row.collectionPointContactMobileNo || '-'}
                          </Typography>
                        </Box>

                        {/* Telephone (if available) */}
                        {row.collectionPointPhoneNo && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocalPhoneIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            <Typography variant="body2" color="#64748b">
                              {row.collectionPointPhoneNo}
                            </Typography>
                          </Box>
                        )}

                        {/* Address */}
                        <Box display="flex" alignItems="flex-start" gap={1} mt={0.5}>
                          <LocationOnIcon sx={{ fontSize: 18, color: '#ef4444', flexShrink: 0, mt: '2px' }} />
                          <Tooltip title={row.collectionPointAddress || ''} placement="top">
                            <Typography
                              variant="body2"
                              color="#64748b"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                fontSize: '0.8rem',
                                lineHeight: 1.35,
                              }}
                            >
                              {row.collectionPointAddress || 'No Address Provided'}
                            </Typography>
                          </Tooltip>
                        </Box>

                        {/* GRL Reference */}
                        {row.collectionPointGrl && (
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <PinDropIcon sx={{ fontSize: 16, color: '#d97706' }} />
                            <Typography variant="caption" fontWeight={600} color="#b45309">
                              GRL: {row.collectionPointGrl}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleEdit(row)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          borderColor: '#e2e8f0',
                          color: '#0284c7',
                          bgcolor: '#f8fafc',
                          '&:hover': {
                            bgcolor: '#f0f9ff',
                            borderColor: '#bae6fd',
                          },
                        }}
                      >
                        Edit Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Paper
            elevation={0}
            sx={{
              mt: 2.5,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
      )}

      {/* Modern Add / Edit Modal */}
      {viewDetailPopup && (
        <PickUpPointsDetailsModal
          modalShow={viewDetailPopup}
          setModalShow={setViewDetailPopup}
          dataInfo={selectedPoint}
          isTitle={modalTitle}
          onRefresh={onRefresh}
        />
      )}
    </Box>
  );
};

PickUpPointsDetailsView.propTypes = {
  reactTableData: PropTypes.shape({
    tableHeaderData: PropTypes.arrayOf(PropTypes.shape()),
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  onRefresh: PropTypes.func,
};

export default PickUpPointsDetailsView;
